import { useState, useEffect, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, Users, BarChart2, Target, Zap, Shield, Network } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

/* ── Canvas particle animation ──────────────────────────────── */
function ParticleCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Build particles
    const count = Math.max(60, Math.floor((canvas.width * canvas.height) / 14000));
    const particles = Array.from({ length: count }, () => ({
      x:      Math.random() * canvas.width,
      y:      Math.random() * canvas.height,
      radius: Math.random() * 1.8 + 0.4,
      speedX: (Math.random() - 0.5) * 0.25,
      speedY: (Math.random() - 0.5) * 0.25,
      opacity: Math.random() * 0.55 + 0.15,
      glow:   Math.random() > 0.78,
      color:  Math.random() > 0.5 ? "37,99,235" : "0,212,203",
    }));

    const LINK_DIST = 130;
    let raf;

    const draw = () => {
      // Dark background
      ctx.fillStyle = "#0B1220";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Connection lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < LINK_DIST) {
            const alpha = 0.18 * (1 - d / LINK_DIST);
            ctx.beginPath();
            ctx.strokeStyle = `rgba(37,99,235,${alpha})`;
            ctx.lineWidth   = 0.6;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw & move particles
      for (const p of particles) {
        p.x += p.speedX;
        p.y += p.speedY;
        if (p.x < 0)             p.x = canvas.width;
        if (p.x > canvas.width)  p.x = 0;
        if (p.y < 0)             p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        if (p.glow) {
          const r   = p.radius * 8;
          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
          grad.addColorStop(0, `rgba(${p.color},${p.opacity * 0.7})`);
          grad.addColorStop(1, "transparent");
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fillStyle = grad;
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.glow
          ? `rgba(${p.color},${Math.min(1, p.opacity + 0.4)})`
          : `rgba(200,220,255,${p.opacity * 0.5})`;
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        display: "block",
      }}
    />
  );
}

/* ── Logo component ─────────────────────────────────────────── */
// Uses a pre-cropped PNG (951×197px) — no clipping tricks, renders at natural ratio
const LogoImage = ({ size = "md" }) => {
  const height = size === "lg" ? 56 : 46;
  return (
    <img
      src="/logo-gr-horizontal.png"
      alt="GridRecruiter"
      style={{ height, width: "auto", display: "block", flexShrink: 0 }}
      draggable={false}
    />
  );
};

const FEATURES = [
  { icon: Users,    label: "Perfis" },
  { icon: BarChart2, label: "Análises" },
  { icon: Target,   label: "Precisão" },
  { icon: Zap,      label: "Agilidade" },
  { icon: Shield,   label: "Confiança" },
  { icon: Network,  label: "Conexão" },
];

/* ── Page component ─────────────────────────────────────────── */
const LoginPage = () => {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email,      setEmail]      = useState("");
  const [password,   setPassword]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");

  useDocumentTitle("Entrar — GridRecruiter");

  if (!loading && user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível autenticar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", position: "relative", overflow: "hidden" }}>
      {/* Particle canvas fills entire screen */}
      <ParticleCanvas />

      {/* ── Left panel — brand content ─────────────────────── */}
      <div
        style={{
          flex: "0 0 58%",
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "60px 64px",
        }}
        className="hidden-mobile"
      >
        {/* Logo */}
        <div style={{ marginBottom: 12 }}>
          <LogoImage size="lg" />
        </div>
        <div style={{
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginBottom: 40,
        }}>
          <span style={{ color: "#00D4CB" }}>Conectamos talentos,</span>{" "}
          <span style={{ color: "#8A93A6" }}>impulsionamos resultados.</span>
        </div>

        {/* Badge */}
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          background: "rgba(37,99,235,0.15)",
          border: "1px solid rgba(37,99,235,0.35)",
          borderRadius: 999,
          padding: "5px 14px",
          marginBottom: 28,
          width: "fit-content",
        }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#00D4CB", display: "inline-block" }} />
          <span style={{ fontSize: 11, color: "#00D4CB", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}>
            Plataforma de Recrutamento com IA
          </span>
        </div>

        {/* Headline */}
        <h1 style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 800,
          fontSize: 46,
          lineHeight: 1.08,
          letterSpacing: "-0.035em",
          color: "#fff",
          margin: "0 0 12px",
        }}>
          Conectamos<br />
          <span style={{ background: "linear-gradient(90deg, #2563EB, #00D4CB)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            talentos
          </span>{" "}
          aos<br />
          melhores resultados.
        </h1>

        <p style={{ fontSize: 16, color: "rgba(255,255,255,0.5)", margin: "0 0 40px", lineHeight: 1.6, maxWidth: 420 }}>
          Recrutamento inteligente com IA — triagem de currículos, sourcing no LinkedIn
          e comunicação automatizada via WhatsApp.
        </p>

        {/* Feature chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {FEATURES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.10)",
                borderRadius: 8,
                padding: "7px 14px",
              }}
            >
              <Icon style={{ width: 15, height: 15, color: "#00D4CB" }} strokeWidth={2} />
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ position: "absolute", bottom: 32, left: 64 }}>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.2)" }}>
            © 2026 GridRecruiter — Todos os direitos reservados
          </span>
        </div>
      </div>

      {/* ── Right panel — login form ──────────────────────── */}
      <div
        style={{
          flex: "0 0 42%",
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 32px",
          background: "rgba(9, 16, 29, 0.65)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          borderLeft: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <div style={{ width: "100%", maxWidth: 380 }}>
          {/* Mobile-only logo */}
          <div style={{ marginBottom: 32 }} className="show-mobile-only">
            <LogoImage size="md" />
          </div>

          <h2 style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 800,
            fontSize: 28,
            letterSpacing: "-0.03em",
            color: "#fff",
            margin: "0 0 6px",
          }}>
            Bem-vindo 👋
          </h2>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.45)", margin: "0 0 32px" }}>
            Acesse sua conta para continuar
          </p>

          <form onSubmit={handleSubmit} data-testid="login-form" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label htmlFor="email" className="login-label">E-mail corporativo</label>
              <input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="seu@empresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="login-input"
              />
            </div>

            <div>
              <label htmlFor="password" className="login-label">Senha</label>
              <input
                id="password"
                data-testid="login-password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="login-input"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "10px 14px",
                  borderRadius: 10,
                  background: "rgba(239,68,68,0.12)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  color: "#fca5a5",
                  fontSize: 13,
                }}
              >
                <AlertCircle style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="login-btn"
              style={{ marginTop: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              {submitting && <Loader2 style={{ width: 18, height: 18, animation: "spin 1s linear infinite" }} />}
              {submitting ? "Entrando…" : "⇢  Entrar no Sistema"}
            </button>
          </form>

          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Shield style={{ width: 13, height: 13, color: "#22C55E" }} />
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>Conexão segura e criptografada</span>
            </div>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.22)" }}>
              Problemas de acesso? Contate o administrador
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .hidden-mobile { display: none !important; }
          .show-mobile-only { display: block !important; }
        }
        .show-mobile-only { display: none; }
      `}</style>
    </div>
  );
};

export default LoginPage;
